import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

const STRIPE_MIN_CENTS = 50;

function schoolIdFromReward(reward) {
  const s = reward?.school;
  if (s == null || s === '') return null;
  if (typeof s === 'object' && s.id != null) return String(s.id);
  return String(s);
}

const Rewards = () => {
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('available');
  const [redeeming, setRedeeming] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(null);
  const [user, setUser] = useState(apiService.getCurrentUser());
  const [pointsSummary, setPointsSummary] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_success') === '1') {
      alert(
        'Purchase successful! Thank you for your purchase. Your reward is being processed. Reload or refresh the list if needed.',
      );
      window.history.replaceState(null, '', window.location.pathname);
    } else if (params.get('stripe_cancel') === '1') {
      alert('Checkout was canceled.');
      window.history.replaceState(null, '', window.location.pathname);
    }

    const fetchData = async () => {
      try {
        const [rewardsData, redemptionsData, freshUser] = await Promise.all([
          apiService.getRewards(),
          apiService.getRedemptions(),
          apiService.fetchCurrentUser()
        ]);
        setRewards(rewardsData.results || rewardsData);
        setRedemptions(redemptionsData.results || redemptionsData);
        setUser(freshUser);

        if (freshUser?.id) {
          try {
            const pointsData = await apiService.getUserPointsSummary(freshUser.id);
            setPointsSummary(pointsData);
          } catch (error) {
            console.error('Failed to fetch points summary:', error);
          }
        }
      } catch (error) {
        console.error('Failed to fetch rewards data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleRedeem = async (reward) => {
    const availablePoints = pointsSummary?.available_points || 0;
    if (reward.points_cost > availablePoints) {
      alert(`Insufficient points. You have ${availablePoints} points available, but this reward costs ${reward.points_cost} points.`);
      return;
    }

    if (!window.confirm(`Redeem "${reward.title}" for ${reward.points_cost} points?`)) {
      return;
    }

    setRedeeming(reward.id);

    try {
      await apiService.createRedemption(reward.id, reward.points_cost);

      const [rewardsData, redemptionsData, freshUser] = await Promise.all([
        apiService.getRewards(),
        apiService.getRedemptions(),
        apiService.fetchCurrentUser()
      ]);
      setRewards(rewardsData.results || rewardsData);
      setRedemptions(redemptionsData.results || redemptionsData);
      setUser(freshUser);

      if (freshUser?.id) {
        try {
          const pointsData = await apiService.getUserPointsSummary(freshUser.id);
          setPointsSummary(pointsData);
        } catch (error) {
          console.error('Failed to refresh points summary:', error);
        }
      }

      alert('Reward redeemed successfully!');
    } catch (error) {
      console.error('Failed to redeem reward:', error);

      if (error.response?.data?.points_spent) {
        alert(error.response.data.points_spent[0]);
      } else if (error.response?.data?.reward) {
        alert(error.response.data.reward[0]);
      } else {
        alert('Failed to redeem reward. Please try again.');
      }
    } finally {
      setRedeeming(null);
    }
  };

  const handleStripePay = async (reward) => {
    if (stripeLoading) return;
    const stripeCents = Number(reward?.cash_price_cents);
    const usd = Number.isFinite(stripeCents) ? (stripeCents / 100).toFixed(2) : '';
    if (
      !window.confirm(
        `Purchase "${reward.title}" for $${usd} with card?\n\nYou will complete payment securely on Stripe on the next page. Points will not be charged.`,
      )
    ) {
      return;
    }
    setStripeLoading(reward.id);
    try {
      const data = await apiService.createStripeRewardCheckoutSession(reward.id);
      const url = data?.url;
      if (url) {
        window.location.assign(url);
      } else {
        alert('Could not start checkout.');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Checkout failed.');
    } finally {
      setStripeLoading(null);
    }
  };

  const getAvailableRewards = () => {
    return rewards.filter(reward => reward.is_active);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
      case 'fulfilled':
        return '#4caf50';
      case 'pending':
        return '#ff9800';
      case 'cancelled':
      case 'expired':
        return '#f44336';
      default:
        return '#666';
    }
  };

  const getStatusText = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Loading rewards...</h2>
      </div>
    );
  }

  const availableRewards = getAvailableRewards();

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{
        display: 'flex',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        overflow: 'hidden'
      }}>
        <button
          onClick={() => setActiveTab('available')}
          style={{
            flex: 1,
            padding: '1rem 2rem',
            border: 'none',
            backgroundColor: activeTab === 'available' ? '#1976d2' : 'white',
            color: activeTab === 'available' ? 'white' : '#666',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '1rem'
          }}
        >
          Available Rewards ({availableRewards.length})
        </button>
        <button
          onClick={() => setActiveTab('history')}
          style={{
            flex: 1,
            padding: '1rem 2rem',
            border: 'none',
            backgroundColor: activeTab === 'history' ? '#1976d2' : 'white',
            color: activeTab === 'history' ? 'white' : '#666',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '1rem'
          }}
        >
          Redemption History ({redemptions.length})
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ color: '#1976d2', margin: 0 }}>Rewards</h1>
        <div style={{
          backgroundColor: '#1976d2',
          color: 'white',
          padding: '0.75rem 1.5rem',
          borderRadius: '25px',
          fontWeight: 'bold'
        }}>
          {pointsSummary?.available_points || 0} Points Available
        </div>
      </div>

      {activeTab === 'available' && (
        <div>
          {availableRewards.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '2rem' }}>
              {availableRewards.map((reward) => {
                const isRedeeming = redeeming === reward.id;
                const isStripeBusy = stripeLoading === reward.id;
                const availablePoints = pointsSummary?.available_points || 0;
                const isCardOnly = reward.redemption_type === 'card';
                const canAfford = reward.points_cost <= availablePoints;
                const earlyBlocked =
                  user?.active_school?.early_release &&
                  reward.redemption_type !== 'raffle';
                const stripeCents = Number(reward?.cash_price_cents);
                const canPayWithStripe =
                  !earlyBlocked &&
                  Number.isFinite(stripeCents) &&
                  stripeCents >= STRIPE_MIN_CENTS &&
                  (reward.redemption_type === 'first' || isCardOnly);
                const cardLooksOk = isCardOnly ? canPayWithStripe : canAfford || canPayWithStripe;

                return (
                  <div
                    key={reward.id}
                    role="presentation"
                    onClick={() => {
                      apiService
                        .postAnalyticsEvents([
                          {
                            event_type: 'reward_click',
                            payload: {
                              reward_id: reward.id,
                              reward_title: (reward.title || '').slice(0, 200),
                              school_id: schoolIdFromReward(reward),
                              school_name: (reward.school_name || '').slice(0, 200),
                              redemption_type: reward.redemption_type,
                              points_cost: reward.points_cost,
                            },
                          },
                          {
                            event_type: 'reward_detail_view',
                            payload: {
                              reward_id: reward.id,
                              reward_title: (reward.title || '').slice(0, 200),
                              school_id: schoolIdFromReward(reward),
                              school_name: (reward.school_name || '').slice(0, 200),
                              redemption_type: reward.redemption_type,
                              points_cost: reward.points_cost,
                              surface: 'reward_card',
                            },
                          },
                        ])
                        .catch(() => {});
                    }}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      overflow: 'hidden',
                      border: cardLooksOk ? '1px solid #eee' : '1px solid #ffcdd2',
                      opacity: cardLooksOk ? 1 : 0.7
                    }}
                  >
                    <div style={{ padding: '1.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0, color: cardLooksOk ? '#1976d2' : '#999' }}>{reward.title}</h3>
                        <div style={{
                          backgroundColor: '#f5f5f5',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          color: '#666'
                        }}>
                          {reward.reward_type}
                        </div>
                      </div>

                      <p style={{ margin: '1rem 0', color: '#666', fontSize: '0.875rem' }}>
                        {reward.description}
                      </p>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <div>
                          <div
                            style={{
                              fontSize: '1.25rem',
                              fontWeight: 'bold',
                              color: isCardOnly
                                ? canPayWithStripe
                                  ? '#1976d2'
                                  : '#f44336'
                                : canAfford
                                  ? '#1976d2'
                                  : '#f44336',
                            }}
                          >
                            {isCardOnly
                              ? canPayWithStripe
                                ? `$${(stripeCents / 100).toFixed(2)}`
                                : 'Set a valid card price'
                              : (
                                <>
                                  {reward.points_cost} Points
                                  {canPayWithStripe ? ` or $${(stripeCents / 100).toFixed(2)}` : ''}
                                </>
                              )}
                          </div>
                          {reward.merchant_name && (
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              via {reward.merchant_name}
                            </div>
                          )}
                          {!isCardOnly && !canAfford && (
                            <div style={{ fontSize: '0.75rem', color: '#f44336', marginTop: '0.25rem' }}>
                              Need {reward.points_cost - availablePoints} more points
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {!isCardOnly && (
                          <button
                            type="button"
                            onClick={() => handleRedeem(reward)}
                            disabled={isRedeeming || isStripeBusy || !canAfford}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              cursor: (!isRedeeming && !isStripeBusy && canAfford) ? 'pointer' : 'not-allowed',
                              backgroundColor: isRedeeming ? '#ccc' : canAfford ? '#4caf50' : '#ffcdd2',
                              color: canAfford ? 'white' : '#999'
                            }}
                          >
                            {isRedeeming ? 'Redeeming...' : canAfford ? 'Redeem with points' : 'Insufficient points'}
                          </button>
                        )}
                        {canPayWithStripe && (
                          <button
                            type="button"
                            onClick={() => handleStripePay(reward)}
                            disabled={isRedeeming || isStripeBusy}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #1976d2',
                              borderRadius: '4px',
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              cursor: (!isRedeeming && !isStripeBusy) ? 'pointer' : 'not-allowed',
                              backgroundColor: isStripeBusy ? '#e3f2fd' : 'white',
                              color: '#1976d2'
                            }}
                          >
                            {isStripeBusy
                              ? 'Opening checkout…'
                              : `Pay $${(stripeCents / 100).toFixed(2)} with card`}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{
              backgroundColor: 'white',
              padding: '3rem',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center',
              color: '#666'
            }}>
              <h3>No rewards available</h3>
              <p>Check back later for new rewards!</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          {redemptions.length > 0 ? (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                gap: '1rem',
                padding: '1rem 1.5rem',
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
                borderBottom: '2px solid #eee'
              }}>
                <div>Reward</div>
                <div>Points Used</div>
                <div>Status</div>
                <div>Redeemed Date</div>
                <div>Fulfillment</div>
              </div>

              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {redemptions.map((redemption) => (
                  <div
                    key={redemption.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                      gap: '1rem',
                      padding: '1rem 1.5rem',
                      borderBottom: '1px solid #eee',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {redemption.reward_title || 'Unknown Reward'}
                      </div>
                    </div>

                    <div style={{ fontWeight: 'bold', color: '#1976d2' }}>
                      {redemption.points_spent}
                    </div>

                    <div>
                      <span style={{
                        backgroundColor: getStatusColor(redemption.status),
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}>
                        {getStatusText(redemption.status)}
                      </span>
                    </div>

                    <div style={{ fontSize: '0.875rem' }}>
                      {new Date(redemption.requested_at).toLocaleDateString()}
                    </div>

                    <div style={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>
                      {redemption.fulfillment_channel || '-'}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
              <h3>No redemptions yet</h3>
              <p>Start redeeming rewards to see your history here!</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Rewards;
